"use client";

/**
 * AppFrame — the shared app layout: a left side, the main pane, a right side
 * (adr-0023).
 *
 * Apps DECLARE each side and AppFrame decides the rest:
 *
 *   <AppFrame
 *     yield="right"                                   // which side gives way first
 *     left={{ role: "essential", content: "list", title: "Messages", node: <Sidebar /> }}
 *     right={{ role: "supplementary", content: "structured", title: "Outputs" }}
 *   >
 *
 * Widths, docking breakpoints and default open state come from the table in
 * `lib/app-frame-layout.ts` — the one place they are decided, for every app.
 *
 * - **Docked** on wide screens: a plain `aside` beside the main pane. Its
 *   open/closed choice is remembered per app.
 * - **Drawer** below its breakpoint: a Radix Dialog (`Sheet`) — focus trap,
 *   `aria-modal`, Escape and backdrop to close, focus returned. Opening one
 *   drawer closes the other. About 85vw on a phone; the side's own width from
 *   640px, where 85vw would cover a tablet's content.
 *
 * **A side's content keeps its state across breakpoints.** It is portalled
 * ONCE into a node AppFrame owns, and that node is moved (`appendChild`)
 * between the docked `aside` and the drawer. Rendering the same element into
 * two places would remount it — `memo` does not prevent that — so a collab
 * editor would reconnect and a half-typed draft would vanish on rotate.
 *
 * The right side is filled by SLOTS: a pane renders `<AppFrameRightSlot>` in
 * its own tree and the content portals into the panel, keeping the pane's
 * context. Several slots become tabs. A slot with its own header passes
 * `chrome="own"` and closes itself with `<AppFrameClose side="right" />`.
 *
 * State lives in `AppFrameProvider`, which `RootLayout` mounts for every app,
 * so any component can drive either side through `useAppFrame()`.
 */

import * as React from "react";
import { createPortal } from "react-dom";
import { PanelLeft, PanelRight, X } from "lucide-react";

import { cn } from "@workspace/ui/lib/utils";
import { minWidth, useMediaQuery } from "@workspace/ui/hooks/useMediaQuery";
import {
  DRAWER_FULL_FROM,
  DRAWER_PHONE_WIDTH,
  SIDE_WIDTH,
  dockThresholds,
  openByDefault,
  type Side,
  type SideContent,
  type SideRole,
  type YieldSide,
} from "@workspace/ui/lib/app-frame-layout";
import { Sheet, SheetContent, SheetTitle } from "@workspace/ui/components/sheet";

export type { Side, SideContent, SideRole, YieldSide };

// ── Public types ─────────────────────────────────────────────────────────────

/** What a side IS. Everything numeric follows from it (adr-0023). */
export interface AppFrameSideSpec {
  role: SideRole;
  content: SideContent;
  /** Names the side — the drawer's accessible name, the mobile button's label. */
  title: string;
}

export interface AppFrameSide {
  /** Declared by the current AppFrame. */
  present: boolean;
  /** Visible now: docked and not collapsed, or a drawer that is open. */
  open: boolean;
  /** Wide enough to dock beside the main pane rather than cover it. */
  docked: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

export interface AppFrameSlotMeta {
  id: string;
  /** Always required: it is the drawer's accessible name, even with own chrome. */
  title: string;
  /** A count or short label next to the title and on the toggle. */
  badge?: string | number;
  /**
   * `frame` (default): AppFrame draws a header with the slot tabs and a close
   * button. `own`: the content brings its own header, and closes itself with
   * `AppFrameClose`.
   */
  chrome?: "frame" | "own";
}

export interface AppFrameRight extends AppFrameSide {
  slots: AppFrameSlotMeta[];
  /** The slot being shown, or undefined when closed or empty. */
  activeId: string | undefined;
  /** Open the panel on a slot. */
  show: (id: string) => void;
}

export interface AppFrameValue {
  left: AppFrameSide;
  right: AppFrameRight;
}

// ── State ────────────────────────────────────────────────────────────────────

/**
 * Where the left side's toggle lives.
 *
 * `header` (the default) puts it in the global header, which is on every route
 * — the safe choice, and the only one for an app whose panes have no chrome of
 * their own. `pane` says the app puts `AppFrameToggle side="left"` in its own
 * pane headers instead, and suppresses the global one so there is never a
 * second button for the same side.
 *
 * An app choosing `pane` owns the consequence: EVERY pane it can route to needs
 * a toggle, terminal error states included, or a viewer who collapsed the
 * sidebar has no way back — the choice is remembered, so a reload does not help.
 */
export type LeftToggleSite = "header" | "pane";

interface FrameConfig {
  yield: YieldSide;
  left?: AppFrameSideSpec;
  right?: AppFrameSideSpec;
  leftToggle: LeftToggleSite;
}

interface Internal extends AppFrameValue {
  config: FrameConfig | null;
  setConfig: (config: FrameConfig | null) => void;
  widths: Record<Side, number>;
  register: (meta: AppFrameSlotMeta) => () => void;
  body: HTMLElement | null;
  setBody: (el: HTMLElement | null) => void;
}

const Ctx = React.createContext<Internal | null>(null);

function useInternal(): Internal {
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error("AppFrame components need an <AppFrameProvider> (RootLayout mounts one)");
  return ctx;
}

/** Either side's state and controls, from anywhere under the provider. */
export function useAppFrame(): AppFrameValue {
  const { left, right } = useInternal();
  return { left, right };
}

type Prefs = Partial<Record<Side, boolean>>;

function readPrefs(key: string): Prefs {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Prefs) : {};
  } catch {
    return {};
  }
}

function writePrefs(key: string, prefs: Prefs): void {
  try {
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    // A per-viewer convenience; losing it only means the default next time.
  }
}

export type AppFrameProviderProps = {
  children: React.ReactNode;
  /** Namespaces the remembered docked state, so apps do not share it. */
  storageKey?: string;
};

export function AppFrameProvider({ children, storageKey = "app-frame" }: AppFrameProviderProps) {
  const [config, setConfig] = React.useState<FrameConfig | null>(null);

  // Remembered docked choices. Read in the initialiser without a hydration
  // mismatch: they only affect output while a side is docked, and during
  // hydration every threshold query reports the server snapshot (narrow).
  const [prefs, setPrefs] = React.useState<Prefs>(() =>
    typeof window === "undefined" ? {} : readPrefs(storageKey),
  );
  // ONE drawer at a time: opening a side replaces whichever was open.
  const [drawer, setDrawer] = React.useState<Side | null>(null);

  // ── Right-panel slots ──
  const [slots, setSlots] = React.useState<AppFrameSlotMeta[]>([]);
  const [chosenId, setChosenId] = React.useState<string | undefined>();
  const [body, setBody] = React.useState<HTMLElement | null>(null);

  const register = React.useCallback((meta: AppFrameSlotMeta) => {
    setSlots((prev) => {
      const i = prev.findIndex((s) => s.id === meta.id);
      if (i === -1) return [...prev, meta];
      const existing = prev[i];
      if (
        existing?.title === meta.title &&
        existing.badge === meta.badge &&
        existing.chrome === meta.chrome
      ) {
        return prev;
      }
      return prev.map((s) => (s.id === meta.id ? meta : s));
    });
    return () => setSlots((prev) => prev.filter((s) => s.id !== meta.id));
  }, []);

  // A side is present when the frame declares it — and, for the right, when
  // something is there to show. An empty right side must not hold the other
  // side's docking back.
  const presentLeft = Boolean(config?.left);
  const presentRight = Boolean(config?.right) && slots.length > 0;
  const thresholds = config
    ? dockThresholds(config.yield, {
        left: presentLeft ? config.left : undefined,
        right: presentRight ? config.right : undefined,
      })
    : { left: null, right: null };
  // A side's OWN threshold if it were present, so `docked` answers "would it
  // dock" even while the right panel is empty (a slot deciding whether to
  // render at all can ask).
  const ownThresholds = config
    ? dockThresholds(config.yield, {
        left: config.left,
        right: config.right,
      })
    : { left: null, right: null };
  const leftDocked = useMediaQuery(minWidth(presentLeft ? thresholds.left : ownThresholds.left));
  const rightDocked = useMediaQuery(minWidth(presentRight ? thresholds.right : ownThresholds.right));

  const setOpenFor = React.useCallback(
    (side: Side, docked: boolean, open: boolean) => {
      if (docked) {
        setPrefs((prev) => {
          const next = { ...prev, [side]: open };
          writePrefs(storageKey, next);
          return next;
        });
      } else {
        setDrawer((current) => (open ? side : current === side ? null : current));
      }
    },
    [storageKey],
  );

  const sideState = (side: Side, present: boolean, docked: boolean): AppFrameSide => {
    const spec = config?.[side];
    const pref = prefs[side] ?? (spec ? openByDefault(spec.role) : false);
    const open = present && (docked ? pref : drawer === side);
    return {
      present,
      open,
      docked,
      setOpen: (next) => setOpenFor(side, docked, next),
      toggle: () => setOpenFor(side, docked, !open),
    };
  };

  const left = sideState("left", presentLeft, leftDocked);
  const rightBase = sideState("right", presentRight, rightDocked);
  // Derived rather than stored: the chosen slot may have unmounted.
  const activeId = !rightBase.open
    ? undefined
    : slots.some((s) => s.id === chosenId)
      ? chosenId
      : slots[0]?.id;

  const value: Internal = {
    config,
    setConfig,
    widths: {
      left: config?.left ? SIDE_WIDTH[config.left.content] : SIDE_WIDTH.list,
      right: config?.right ? SIDE_WIDTH[config.right.content] : SIDE_WIDTH.list,
    },
    left,
    right: {
      ...rightBase,
      open: activeId !== undefined,
      slots,
      activeId,
      show: (id: string) => {
        setChosenId(id);
        setOpenFor("right", rightDocked, true);
      },
    },
    register,
    body,
    setBody,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ── Layout ───────────────────────────────────────────────────────────────────

export type AppFrameProps = Omit<React.ComponentProps<"div">, "left" | "right"> & {
  /** Which side gives way first as the viewport narrows. */
  yield?: YieldSide;
  /** The left side and its content. */
  left?: AppFrameSideSpec & { node: React.ReactNode };
  /** The right side. Its content comes from `AppFrameRightSlot`s. */
  right?: AppFrameSideSpec;
  /**
   * Where the left side's toggle goes. `"pane"` suppresses the global header's
   * one, for an app that puts `AppFrameToggle side="left"` in its own pane
   * headers — read `LeftToggleSite` before choosing it.
   */
  leftToggle?: LeftToggleSite;
  /**
   * The slim bar above the main pane, naming the left side while it is a
   * drawer. Renders only with a `mobileTitle` — turn it off when the app's own
   * panes have headers that already say where you are.
   */
  mobileBar?: boolean;
  /** Title shown in that bar; without one there is no bar. */
  mobileTitle?: React.ReactNode;
};

const noopSubscribe = () => () => {};

/**
 * A node the content is portalled into once, and moved between sites.
 *
 * Withheld until hydration has finished. The server has no `document`, so it
 * renders no portal; if the client returned its host on the hydration pass, the
 * portal would render there and React would report a hydration mismatch and
 * re-render the whole frame client-side — on every prerendered page.
 * `useSyncExternalStore` answers the server snapshot (`false`) while
 * hydrating, then re-renders with `true`.
 */
function useContentHost(): HTMLDivElement | null {
  const hydrated = React.useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  );
  const [host] = React.useState<HTMLDivElement | null>(() => {
    if (typeof document === "undefined") return null;
    const el = document.createElement("div");
    el.dataset.slot = "app-frame-content";
    el.style.cssText = "display:flex;flex-direction:column;flex:1;min-height:0;width:100%";
    return el;
  });
  return hydrated ? host : null;
}

/** A callback ref that moves `host` into the element as it mounts. */
function mountInto(host: HTMLElement | null) {
  return (el: HTMLElement | null) => {
    if (el && host && host.parentNode !== el) el.appendChild(host);
  };
}

export function AppFrame({
  yield: yieldSide = "right",
  left,
  right,
  leftToggle = "header",
  mobileBar = true,
  mobileTitle,
  className,
  children,
  ...props
}: AppFrameProps) {
  const frame = useInternal();

  // Register the declaration. A layout effect, so the provider's thresholds
  // are known before the first paint — no frame drawn with the wrong layout.
  const leftSpec = left ? { role: left.role, content: left.content, title: left.title } : undefined;
  const configKey = JSON.stringify({ yieldSide, leftSpec, right, leftToggle });
  const { setConfig } = frame;
  // Keyed on the serialised declaration, so an inline `left={{…}}` literal
  // (a new object every render) does not re-register on every render.
  React.useLayoutEffect(() => {
    const declared = JSON.parse(configKey) as {
      yieldSide: YieldSide;
      leftSpec?: AppFrameSideSpec;
      right?: AppFrameSideSpec;
      leftToggle: LeftToggleSite;
    };
    setConfig(
      declared.leftSpec || declared.right
        ? {
            yield: declared.yieldSide,
            left: declared.leftSpec,
            right: declared.right,
            leftToggle: declared.leftToggle,
          }
        : null,
    );
    return () => setConfig(null);
  }, [configKey, setConfig]);

  const leftHost = useContentHost();
  const rightHost = useContentHost();
  const parking = React.useRef<HTMLDivElement>(null);

  // Keep each host in the document while no site shows it, so its content
  // stays connected (focus, measurement, observers) rather than detached.
  React.useLayoutEffect(() => {
    for (const host of [leftHost, rightHost]) {
      if (host && !host.isConnected && parking.current) parking.current.appendChild(host);
    }
  });

  const phoneDrawer = !useMediaQuery(`(min-width: ${DRAWER_FULL_FROM}px)`);

  const renderSide = (side: Side, spec: AppFrameSideSpec | undefined, host: HTMLDivElement | null) => {
    const state = frame[side];
    if (!spec || !state.present) return null;
    const width = frame.widths[side];
    if (state.docked) {
      if (!state.open) return null;
      return (
        <aside
          data-slot={`app-frame-${side}`}
          aria-label={spec.title}
          style={{ width }}
          className={cn(
            "flex min-h-0 shrink-0 flex-col bg-background",
            side === "left" ? "border-r border-border" : "border-l border-border",
          )}
        >
          <div ref={mountInto(host)} className="flex min-h-0 flex-1 flex-col" />
        </aside>
      );
    }
    return (
      <Sheet open={state.open} onOpenChange={(open) => state.setOpen(open)}>
        <SheetContent
          side={side}
          showClose={false}
          aria-describedby={undefined}
          data-slot={`app-frame-${side}-drawer`}
          style={{ width: phoneDrawer ? DRAWER_PHONE_WIDTH : width }}
          className="max-w-none gap-0 p-0 sm:max-w-none"
        >
          <SheetTitle className="sr-only">{spec.title}</SheetTitle>
          <div ref={mountInto(host)} className="flex min-h-0 flex-1 flex-col" />
        </SheetContent>
      </Sheet>
    );
  };

  // Title only, and only when there is a title: the toggle itself now lives in
  // the global header (`AppFrameHeaderToggle`), which is present on every route
  // and at every width. Two buttons for one side read as two different sides.
  const showMobileBar =
    mobileBar && Boolean(mobileTitle) && frame.left.present && !frame.left.docked;

  return (
    <div
      data-slot="app-frame"
      className={cn(
        "flex h-[calc(100dvh-var(--global-header-height,54px))] overflow-hidden",
        className,
      )}
      {...props}
    >
      {renderSide("left", leftSpec, leftHost)}

      {/* `min-h-0`/`min-w-0` are load-bearing: a flex item's default minimum
          is its content, so without them a scroller inside never gets a
          bounded box and the page clips against the `overflow-hidden` above. */}
      <div data-slot="app-frame-main" className="flex min-h-0 min-w-0 flex-1 flex-col">
        {showMobileBar && (
          <div
            data-slot="app-frame-mobile-bar"
            className="flex items-center gap-2 border-b border-border px-3 py-1.5"
          >
            <span className="truncate text-sm font-semibold text-foreground">{mobileTitle}</span>
          </div>
        )}
        {children}
      </div>

      {renderSide("right", right, rightHost)}

      {/* Where content waits while no site is showing it. */}
      <div ref={parking} hidden data-slot="app-frame-parking" />

      {left && leftHost && createPortal(left.node, leftHost)}
      {right && rightHost && createPortal(<RightPanelBody />, rightHost)}
    </div>
  );
}

/** The right side's content: a tab header (unless the slot has its own) and the slot body. */
function RightPanelBody() {
  const frame = useInternal();
  const { slots, activeId, show, setOpen } = frame.right;
  const active = slots.find((s) => s.id === activeId);

  return (
    <>
      {active?.chrome !== "own" && (
        <header className="flex shrink-0 items-center gap-1 border-b border-border px-2 py-2">
          <div role="tablist" className="flex min-w-0 flex-1 items-center gap-1">
            {slots.map((slot) => (
              <button
                key={slot.id}
                type="button"
                role="tab"
                aria-selected={slot.id === activeId}
                onClick={() => show(slot.id)}
                className={cn(
                  "inline-flex items-center gap-1.5 truncate rounded px-2 py-1 text-sm",
                  slot.id === activeId
                    ? "font-semibold text-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                {slot.title}
                {slot.badge !== undefined && (
                  <span className="text-xs font-normal tabular-nums text-muted-foreground">
                    {slot.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close panel"
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
      )}
      {/* Keyed on the slot so switching tabs resets scroll rather than
          carrying one slot's position into another's content. */}
      <div key={activeId} ref={frame.setBody} className="flex min-h-0 flex-1 flex-col overflow-y-auto" />
    </>
  );
}

// ── Slots, toggles, close ────────────────────────────────────────────────────

/**
 * Contribute content to the right side while this is mounted. The children
 * render only while their slot is the one showing, so a closed panel costs
 * nothing beyond the registration.
 */
export function AppFrameRightSlot({
  id,
  title,
  badge,
  chrome = "frame",
  children,
}: AppFrameSlotMeta & { children: React.ReactNode }) {
  const { register, right, body } = useInternal();

  React.useEffect(() => register({ id, title, badge, chrome }), [register, id, title, badge, chrome]);

  if (right.activeId !== id || !body) return null;
  return createPortal(children, body);
}

export type AppFrameToggleProps = React.ComponentProps<"button"> & {
  side: Side;
  /**
   * For the right side: which slot to show. Omitted, the button opens the
   * panel on whatever slot it would show anyway.
   */
  slot?: string;
  /** For assistive tech; defaults to the side's declared title. */
  label?: string;
  badge?: string | number;
  /** Show the side's title beside the icon — for an essential side on mobile. */
  showLabel?: boolean;
};

/** Opens or closes one side. Works docked or as a drawer. */
export function AppFrameToggle({
  side,
  slot,
  label,
  badge,
  showLabel = false,
  className,
  ...props
}: AppFrameToggleProps) {
  const frame = useInternal();
  const { left, right } = frame;
  const showing = side === "left" ? left.open : slot ? right.activeId === slot : right.open;
  const name = label ?? frame.config?.[side]?.title ?? (side === "left" ? "sidebar" : "panel");
  const Icon = side === "left" ? PanelLeft : PanelRight;

  return (
    <button
      type="button"
      data-slot="app-frame-toggle"
      onClick={() => {
        if (side === "left") left.toggle();
        else if (showing) right.setOpen(false);
        else if (slot) right.show(slot);
        else right.setOpen(true);
      }}
      aria-pressed={showing}
      aria-label={showing ? `Hide ${name}` : `Show ${name}`}
      className={cn(
        "inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground aria-pressed:text-foreground",
        className,
      )}
      {...props}
    >
      <Icon className="size-4" />
      {showLabel && <span className="text-sm">{name}</span>}
      {badge !== undefined && <span className="tabular-nums">{badge}</span>}
    </button>
  );
}

/**
 * The left side's toggle for the GLOBAL header (`AppHeader`), so reopening a
 * closed sidebar never depends on which route you are on.
 *
 * The frame's own mobile bar only exists while the side is a drawer, and a
 * DOCKED side that the viewer collapsed renders nothing at all — between them,
 * an app with no toggle of its own had no way back to its sidebar except
 * navigating somewhere that happened to show one. This is that way back, and it
 * is in the one piece of chrome every route shares.
 *
 * Tolerates having no provider above it (`FloatingHeader` renders `AppHeader`
 * outside one) and no declared left side, rendering nothing in either case —
 * a header button for a frame that does not exist would do nothing when pressed.
 */
export function AppFrameHeaderToggle({ className, ...props }: Omit<AppFrameToggleProps, "side">) {
  const ctx = React.useContext(Ctx);
  if (!ctx?.left.present) return null;
  // The app puts this side's toggle in its own pane headers instead
  // (`leftToggle="pane"`). Two buttons for one side read as two sides.
  if (ctx.config?.leftToggle === "pane") return null;
  return (
    <AppFrameToggle
      side="left"
      className={cn("rounded-full p-2", className)}
      {...props}
    />
  );
}

export type AppFrameCloseProps = React.ComponentProps<"button"> & {
  side: Side;
};

/**
 * Closes a side — for content with its own chrome (`chrome="own"`), which has
 * no frame header to close it from. Children replace the default icon.
 */
export function AppFrameClose({ side, className, children, ...props }: AppFrameCloseProps) {
  const frame = useInternal();
  const name = frame.config?.[side]?.title ?? (side === "left" ? "sidebar" : "panel");
  return (
    <button
      type="button"
      data-slot="app-frame-close"
      onClick={() => frame[side].setOpen(false)}
      aria-label={`Close ${name}`}
      className={cn(
        "inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground",
        className,
      )}
      {...props}
    >
      {children ?? <X className="size-4" />}
    </button>
  );
}
